import Mustache from 'mustache';
import Cookies from 'js-cookie';
import A11yDialog from 'a11y-dialog';

const cookieConsent = (() => {
  const COOKIE = 'palmia_cookie_consent'; // Cookie name for storing user's settings
  const COOKIE_TYPES = ['cookies_statistical', 'cookies_ad']; // Cookie types for consent. Must match form input names.

  let consentFormDialog;

  /**
   * Pushes objects to datalayer for GTM
   *
   * @param {array} data - Array of accepted cookie types
   */
  function triggerGtm(data = []) {
    window.dataLayer = window.dataLayer || [];

    if (data.length) {
      const datalayerData = {
        event: 'cookie_consent'
      };

      data.forEach(cookieType => {
        datalayerData[cookieType] = true;
      });

      window.dataLayer.push(datalayerData);
    }
  }

  /**
   * Formats form data for GTM
   * Assumes form data only contains checkboxes
   *
   * @param {object} data - Form data as key-value pairs
   *
   * @return {array} formattedData
   */
  function formatFormData(data) {
    const formattedData = [];

    Object.keys(data).forEach(field => {
      if (data[field] === '1') {
        formattedData.push(field);
      }
    });

    return formattedData;
  }

  /**
   * Gets form field values as name-value pairs
   *
   * @param {HTMLElement} form - Form element
   *
   * @return {object}
   */
  function serialize(form) {
    const serialized = {};

    if (form) {
      const formElements = form.elements;
      for (let i = 0; i < formElements.length; i++) {
        const field = formElements[i];
        if (field.name) {
          if (
            (field.type !== 'checkbox' && field.type !== 'radio') ||
            field.checked
          ) {
            serialized[field.name] = field.value;
          }
        }
      }
    }

    return serialized;
  }

  /**
   * Handles consent data
   * Triggers GTM, closes modal, hides notice
   * If Data is empty cookies are set to empty and no statistical and ads are used
   *
   * @param {array} data - Array of accepted cookie types
   */
  function handleConsent(data) {
    // Send values to tag manager
    triggerGtm(data);

    // Save values to cookie
    setCookie(COOKIE, data);

    // Set also hubspot cookies
    handleHubspotCookies(data);

    // Send data to GA4
    sendCookieDataGA4(data);

    // Hide UI elements
    hideForm();
    hideCookieNotice();
  }

  /**
   * PALMIACONT-116, send cookie data to GA4
   * @param {string[]} data - Array of accepted cookie types
   */
  function sendCookieDataGA4(data) {
    let cookie;
    if (data.length < 1) {
      cookie = 'cookie_selection_no_tracking';
    } else if (data.length === COOKIE_TYPES.length ) {
      cookie = 'cookie_selection_all';
    } else if (data.length === 1 && COOKIE_TYPES.includes(data[0])) {
      cookie = 'cookie_selection_' + data[0];
    }
    const url = '/stage-ajax/ga4EventApi/';
    fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        name: cookie,
        params: { non_interaction: true }
      }),
      headers: { 'Content-type': 'application/json' }
    });
  }

  /**
   *
   * @param {array} data - Array of accepted cookie types
   */
  function handleHubspotCookies(data = []) {
    if (data.length === 0) {
      // No cookie types accepted. Allow only hubspot mandatory cookies.
      setCookie('__hs_opt_out', 'yes');
      setCookie('__hs_do_not_track', 'yes');
    } else if (COOKIE_TYPES.every(type => data.includes(type))) {
      // All accepted
      setCookie('__hs_opt_out', 'no');
    } else {
      // Some accepted
      if (!data.includes('cookies_ad')) {
        setCookie('__hs_do_not_track', 'yes');
      }
    }
  }

  /**
   * Used to hide hubspots cookie-consent, so that
   * we don't get two consents at the same time.
   */
  function initHubspotCookies() {
    const value = getCookie('__hs_initial_opt_in');

    if (typeof value === 'undefined') {
      setCookie('__hs_initial_opt_in', 'true');
    }
  }

  /**
   * Handles cookie form submit event
   *
   * @param {Event} event - Submit event
   */
  function onSubmit(event) {
    event.preventDefault();
    const form = event.target;
    const formData = formatFormData(serialize(form));

    handleConsent(formData);
  }

  /**
   * Approves all cookies.
   * A shortcut to selecting all checkboxes on the cookie consent form
   */
  function approveAll() {
    const data = [...COOKIE_TYPES];
    handleConsent(data);
  }

  /**
   * Reject all cookies
   * Sets cookie value to empty array
   */
  function rejectAll() {
    handleConsent([]);
  }

  /**
   * Saves value to a cookie
   *
   * @param {string} name . Cookie name
   * @param {object|array|string} value - Cookie value (will be stringified)
   * @param {object} options - Optional settings object to control cookie. see js-cookie documentation.
   */
  function setCookie(name, value, options) {
    const defaultOptions = {
      expires: 365
    };

    let cookieValue = value;
    if (typeof value !== 'string') {
      cookieValue = JSON.stringify(value);
    }

    Cookies.set(name, cookieValue, Object.assign({}, defaultOptions, options));
  }

  /**
   * Reads cookie value
   *
   * @param {string} cookieName - Name of cookie
   * @return {string} - cookie value
   */
  function getCookie(cookieName) {
    return Cookies.get(cookieName);
  }

  /**
   * Hides form dialog
   */
  function hideForm() {
    if (consentFormDialog) {
      consentFormDialog.hide();
    }
  }

  /**
   * Shows form dialog.
   * If it does not exist in dom, creates it first before showing.
   */
  function showForm() {
    if (!consentFormDialog) {
      createForm();
    }

    consentFormDialog.show();
  }

  /**
   * Creates the form dialog and appends it to dom
   *
   * @return {object} - dialog instance
   */
  function createForm() {
    const templateEl = document.getElementById(
      'mustache-cookie-consent-dialog'
    );
    const template = templateEl ? templateEl.innerHTML : '';
    const markup = Mustache.render(template);

    const modal = document.createElement('div');
    modal.setAttribute('id', 'cookie-consent-dialog');
    modal.setAttribute('class', 'dialog');
    modal.innerHTML = `
      <div class="dialog__overlay" tabindex="-1" data-a11y-dialog-hide></div>
      <dialog class="dialog__content" aria-labelledby="dialog-title">
        <button class="dialog__close-button" type="button" data-a11y-dialog-hide aria-label="Close this dialog window">
          <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path d="M18.984 6.422l-5.578 5.578 5.578 5.578-1.406 1.406-5.578-5.578-5.578 5.578-1.406-1.406 5.578-5.578-5.578-5.578 1.406-1.406 5.578 5.578 5.578-5.578z"></path>
          </svg>
        </button>
        <div class="dialog__content__wrapper">
          ${markup}
        </div>
      </dialog>
    `;

    document.body.appendChild(modal);

    const dialog = new A11yDialog(modal);

    dialog.on('hide', function(dialogEl, event) {
      const consent = getCookie(COOKIE);
      if (!consent) {
        // Cookie does not exist, consent has not been given, show notice again.
        showCookieNotice();
      }
    });

    consentFormDialog = dialog;

    return consentFormDialog;
  }

  /**
   * Hides cookie notice element
   */
  function hideCookieNotice() {
    const notice = document.querySelector('.js-cookie-consent-notice');
    if (notice) {
      notice.style.display = 'none';
    }
  }

  /**
   * Shows cookie notice element
   */
  function showCookieNotice() {
    const notice = document.querySelector('.js-cookie-consent-notice');
    if (notice) {
      notice.style.display = 'flex';
    }
  }

  /**
   * Creates cookie notice element
   */
  function createCookieNotice() {
    const templateEl = document.getElementById(
      'mustache-cookie-consent-notice'
    );
    const template = templateEl ? templateEl.innerHTML : '';
    const markup = Mustache.render(template);
    document.body.insertAdjacentHTML('beforeend', markup);
  }

  /**
   * Checks whether cookie consent has been given previously
   *
   * @return {object|boolean} - Cookie consent settings, or false if no consent given
   */
  function checkExistingConsent() {
    const consentCookie = getCookie(COOKIE);

    if (consentCookie) {
      try {
        const cookieSettings = JSON.parse(consentCookie);
        return cookieSettings;
      } catch (e) {
        return false;
      }
    } else {
      return false;
    }
  }

  /**
   * Inits cookie consent functionality.
   */
  function init() {
    initHubspotCookies();

    const consent = checkExistingConsent();

    if (consent) {
      // Consent already given, trigger GTM immediately
      triggerGtm(consent);
    } else {
      // Consent not given, show notice
      createCookieNotice();
    }

    // Add event listeners
    document.addEventListener(
      'click',
      function(event) {
        if (event.target.matches('.js--cookie-consent--approve-all')) {
          approveAll();
        }
      },
      false
    );

    document.addEventListener(
      'click',
      function(event) {
        if (event.target.matches('.js--cookie-consent--reject-all')) {
          rejectAll();
        }
      },
      false
    );

    document.addEventListener('click', function(event) {
      if (event.target.matches('.js--cookie-consent--open-form')) {
        hideCookieNotice();
        showForm();
      }
    });

    document.addEventListener('submit', function(event) {
      if (event.target.matches('.js--cookie-consent--form')) {
        onSubmit(event);
      }
    });

    // Finds button element in wysiwyg content, sets styles and attaches listener to open cookie settings
    const openBtn = document.querySelector('button[data-open-cookie-consent]');

    if (openBtn) {
      openBtn.classList.add('primary-btn');
      openBtn.style.border = 0;

      openBtn.addEventListener('click', () => {
        hideCookieNotice();
        showForm();
      });
    }
  }

  return {
    init
  };
})();

export default cookieConsent;
